// src\modules\Products\List\components\modals\LocationsStockModal.tsx
import { Badge } from '@shared/components/ui/badge';
import { Button } from '@shared/components/ui/button';
import { Input } from '@shared/components/ui/input';
import { ModalCenter, ModalCenterRef } from '@shared/components/ui/ModalCenter';
import { faBox, faPen, faPlus } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { usePermissions } from '@shared/hooks/usePermissions';
import { useQueryClient } from '@tanstack/react-query';
import { forwardRef, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LocationBatchBadge } from '@modules/Locations/components/display/LocationBatchBadge';
import { getPrimaryDisplayableStockBatch, type LocationStockBatchEntry } from '@modules/Locations/models/Location';
import { useProductStockLocations } from '../../hooks/useProductStockLocations';
import { AddStockToLocationModal, AddStockToLocationModalRef } from './AddStockToLocationModal';
import { AdjustStockModal, AdjustStockModalRef } from './AdjustStockModal';

export interface LocationsStockModalRef {
  open: (productId: string | number) => void;
}

interface StockLocation {
  id: string;
  name: string;
  typeName: string;
  typeId: string;
  stock: number;
  stock_batches?: LocationStockBatchEntry[];
}

export const LocationsStockModal = forwardRef<LocationsStockModalRef, { refetch?: () => void }>(({ refetch }, ref) => {
  const { t } = useTranslation('productsModule');
  const { getTeamType } = usePermissions();
  const modalRef = useRef<ModalCenterRef>(null);
  const adjustStockModalRef = useRef<AdjustStockModalRef>(null);
  const addStockModalRef = useRef<AddStockToLocationModalRef>(null);
  const queryClient = useQueryClient();

  const [productId, setProductId] = useState<string | number | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // Solo tenant_3pl puede ajustar stock
  const canAdjustStock = getTeamType() === 'tenant_3pl';

  useImperativeHandle(ref, () => ({
    open: (id: string | number) => {
      setProductId(id);
      setSearchTerm('');
      setIsOpen(true);
      modalRef.current?.openModal();
    },
  }));

  const { locations, total, isLoading, error, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useProductStockLocations(productId, searchTerm, isOpen);

  const handleAdjustStock = (location: StockLocation) => {
    if (!productId) return;

    adjustStockModalRef.current?.open(
      {
        productId,
        locationId: location.id,
        locationCode: location.name,
        currentQty: location.stock,
      },
      () => {
        // Refrescar la query de ubicaciones después de ajustar
        queryClient.invalidateQueries({
          queryKey: ['locations-by-product', productId],
        });
        // Refrescar la lista principal si se proporciona la función
        if (refetch) {
          refetch();
        }
      },
    );
  };

  const handleAddStock = () => {
    addStockModalRef.current?.open(() => {
      queryClient.invalidateQueries({
        queryKey: ['locations-by-product', productId],
      });
      queryClient.invalidateQueries({
        queryKey: ['product-stock-location-ids', productId],
      });
      if (refetch) {
        refetch();
      }
    });
  };

  const handleCloseModal = () => {
    setIsOpen(false);
    setSearchTerm('');
  };

  const hasSearch = searchTerm.trim().length > 0;
  const showEmptySearch = !isLoading && !error && locations.length === 0 && hasSearch;
  const showEmptyStock = !isLoading && !error && locations.length === 0 && !hasSearch && total === 0;

  const getBadgeClass = (typeId: string) => {
    let badgeClass = 'border-0';
    if (typeId === '2') {
      badgeClass += ' text-badge-status-3 bg-card-border light:bg-badge-bg-status-3';
    } else if (typeId === '1') {
      badgeClass += ' text-badge-status-2 bg-card-border light:bg-badge-bg-status-2';
    }
    return badgeClass;
  };

  const showBatchColumn = useMemo(
    () => locations?.some((location) => getPrimaryDisplayableStockBatch(location) != null) ?? false,
    [locations],
  );

  const gridColsClass = canAdjustStock ? 'grid-cols-12' : 'grid-cols-10';
  const codeColClass = showBatchColumn ? 'col-span-3' : 'col-span-5';
  const typeColClass = showBatchColumn ? 'col-span-2' : 'col-span-3';
  const stockColClass = 'col-span-2';

  return (
    <>
      <ModalCenter
        ref={modalRef}
        title={t('modal.locations_stock_title')}
        icon={<FontAwesomeIcon icon={faBox} className="w-3 h-3" />}
        type="default"
        width="xxl"
        className="h-[100dvh] sm:h-auto w-full sm:max-h-[85vh]"
        onCloseModal={handleCloseModal}
      >
        <div className="space-y-4 pt-3 pb-2">
          <Input
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            placeholder={t('modal.locations_search_placeholder')}
            data-testid="locations-stock-search-input"
          />

          {isLoading && (
            <div className="flex items-center justify-center py-8">
              <p className="text-sm text-subtext">{t('loading.message')}</p>
            </div>
          )}

          {error && (
            <div className="flex items-center justify-center py-8">
              <p className="text-sm text-destructive">{t('toasts.error_title')}</p>
            </div>
          )}

          {!isLoading && !error && locations.length > 0 && (
            <>
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-sm font-medium">
                  {t('modal.locations_list')}
                  {total > 0 ? ` (${locations.length}/${total})` : ''}
                </h3>
                {canAdjustStock && (
                  <Button variant="outline" size="sm" onClick={handleAddStock} data-testid="add-location-button">
                    <FontAwesomeIcon icon={faPlus} className="mr-2 w-3.5" />
                    {t('modal.add_location')}
                  </Button>
                )}
              </div>
              <div className="w-full rounded-md overflow-hidden border border-card-border">
                {/* Header */}
                <div className={`grid text-sm border-b border-card-border py-2 px-3 bg-card-bg ${gridColsClass}`}>
                  <div className={codeColClass}>
                    <div className="font-medium">{t('modal.location_code')}</div>
                  </div>
                  <div className={typeColClass}>
                    <div className="font-medium">{t('modal.location_type')}</div>
                  </div>
                  {showBatchColumn && (
                    <div className="col-span-3">
                      <div className="font-medium">{t('modal.location_batch')}</div>
                    </div>
                  )}
                  <div className={stockColClass}>
                    <div className="font-medium">{t('modal.location_stock')}</div>
                  </div>
                  {canAdjustStock && <div className="col-span-2 font-medium text-right">{t('modal.adjust')}</div>}
                </div>

                {/* Filas */}
                <div>
                  {locations.map((location) => {
                    const primaryBatch = getPrimaryDisplayableStockBatch(location);
                    const stockQty = typeof location.stock === 'number' ? location.stock : 0;

                    return (
                      <div
                        key={location.id}
                        className={`grid border-b border-card-border py-3 px-3 last:border-b-0 items-center ${gridColsClass}`}
                      >
                        <div className={`${codeColClass} text-sm font-medium truncate`}>{location.name}</div>
                        <div className={typeColClass}>
                          <Badge variant="outline" className={getBadgeClass(location.typeId)}>
                            {location.typeName}
                          </Badge>
                        </div>
                        {showBatchColumn && (
                          <div className="col-span-3 min-w-0">
                            {primaryBatch ? <LocationBatchBadge batch={primaryBatch} /> : null}
                          </div>
                        )}
                        <div className={`${stockColClass} font-medium`}>{stockQty}</div>
                        {canAdjustStock && (
                          <div className="col-span-2 flex justify-end">
                            <button
                              type="button"
                              className="p-1 text-icons hover:text-title transition-colors"
                              title={t('modal.adjust')}
                              onClick={() =>
                                handleAdjustStock({
                                  ...location,
                                  stock: stockQty,
                                })
                              }
                              data-testid={`adjust-stock-${location.id}`}
                            >
                              <FontAwesomeIcon icon={faPen} />
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {hasNextPage && (
                <div className="flex justify-center pt-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => fetchNextPage()}
                    disabled={isFetchingNextPage}
                    data-testid="locations-stock-load-more"
                  >
                    {isFetchingNextPage ? t('loading.message') : t('modal.locations_load_more')}
                  </Button>
                </div>
              )}
            </>
          )}

          {showEmptySearch && (
            <div className="flex items-center justify-center py-12">
              <p className="text-sm text-subtext">{t('modal.locations_search_empty')}</p>
            </div>
          )}

          {showEmptyStock && (
            <>
              {canAdjustStock && (
                <div className="flex justify-end mb-4">
                  <Button variant="outline" size="sm" onClick={handleAddStock} data-testid="add-location-button-empty">
                    <FontAwesomeIcon icon={faPlus} className="mr-2 w-3.5" />
                    {t('modal.add_location')}
                  </Button>
                </div>
              )}
              <div className="flex flex-col items-center justify-center text-center py-12">
                <img
                  src="/imagen/empty-tables/empty-product-stock-light.svg"
                  alt="Sin stock"
                  className="w-24 h-24 mb-4 opacity-50"
                />
                <h3 className="text-lg font-medium text-title mb-2">{t('empty.title')}</h3>
                <p className="text-subtext w-[200px]">{t('empty.description')}</p>
              </div>
            </>
          )}
        </div>
      </ModalCenter>

      <AdjustStockModal ref={adjustStockModalRef} />
      <AddStockToLocationModal ref={addStockModalRef} productId={productId} refetch={refetch} />
    </>
  );
});

LocationsStockModal.displayName = 'LocationsStockModal';
